import { Button } from "@/components/ui/Button";
import * as shared from "@/styles/shared";
import { Pencil } from "lucide-react";
import { styles } from "../AddressesPage.styles";
import { useAddresses } from "./AddressesContext";

const EditProviderDialog = () => {
  const { editProviderState, setState, handleEditSave, t } = useAddresses();

  const patch = (partial: Partial<typeof editProviderState>) => setState({ editProviderState: { ...editProviderState, ...partial } });

  const handleClose = () => patch({ open: false });
  const handleDialogClick = (e: React.MouseEvent) => e.stopPropagation();
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => patch({ username: e.target.value });
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => patch({ password: e.target.value });
  const handleAccountKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => patch({ accountKeyword: e.target.value });
  const handleSkipPaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => patch({ skipPayment: e.target.checked });
  const handleMeterReadingEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => patch({ meterReadingEnabled: e.target.checked });
  const handleTaxDeductionChange = (e: React.ChangeEvent<HTMLInputElement>) => patch({ taxDeduction: e.target.checked });
  const handleTaxDeductionAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => patch({ taxDeductionAmount: e.target.value });
  const handleInvoiceEmailToChange = (e: React.ChangeEvent<HTMLInputElement>) => patch({ invoiceEmailTo: e.target.value });
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") handleClose();
  };

  if (!editProviderState.open) return null;

  return (
    <div className={shared.dialogOverlay} onClick={handleClose} onKeyDown={handleKeyDown}>
      <div className={shared.dialog} onClick={handleDialogClick}>
        <div className={styles.dialogHeader}>
          <Pencil className={styles.dialogIcon} />
          <h2 className={shared.headingLg}>{t("addresses:editProvider")}</h2>
        </div>
        <div className={styles.dialogBody}>
          <div className={shared.formStack}>
            <label className={styles.formLabel}>{t("addresses:placeholder.username")}</label>
            <input
              value={editProviderState.username}
              onChange={handleUsernameChange}
              placeholder={t("addresses:placeholder.username")}
              className={shared.inputBase}
            />
            <label className={styles.formLabel}>{t("addresses:placeholder.password")}</label>
            <input
              value={editProviderState.password}
              onChange={handlePasswordChange}
              placeholder={editProviderState.hasPassword ? t("addresses:newPasswordHint") : t("addresses:placeholder.password")}
              type="password"
              autoComplete="off"
              className={shared.inputBase}
            />
            <label className={styles.formLabel}>{t("common:label.accountKeyword")}</label>
            <input
              value={editProviderState.accountKeyword}
              onChange={handleAccountKeywordChange}
              placeholder={t("addresses:placeholder.accountKeyword")}
              className={shared.inputBase}
            />
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={editProviderState.skipPayment} onChange={handleSkipPaymentChange} /> {t("addresses:label.skipPayment")}
            </label>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={editProviderState.meterReadingEnabled} onChange={handleMeterReadingEnabledChange} />{" "}
              {t("addresses:label.meterReadingEnabled")}
            </label>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={editProviderState.taxDeduction} onChange={handleTaxDeductionChange} />{" "}
              {t("addresses:label.taxDeduction")}
            </label>
            {editProviderState.taxDeduction && (
              <input
                value={editProviderState.taxDeductionAmount}
                onChange={handleTaxDeductionAmountChange}
                placeholder={t("placeholder.taxDeductionAmount")}
                type="number"
                step="0.01"
                min="0"
                className={shared.inputBase}
              />
            )}
            <label className={styles.formLabel}>{t("addresses:label.invoiceEmailTo")}</label>
            <input
              value={editProviderState.invoiceEmailTo}
              onChange={handleInvoiceEmailToChange}
              placeholder={t("addresses:placeholder.invoiceEmailTo")}
              type="email"
              autoComplete="off"
              className={shared.inputBase}
            />
          </div>
          <div className={styles.buttonGroup}>
            <Button variant="secondary" onClick={handleClose}>
              {t("addresses:cancel")}
            </Button>
            <Button variant="gradient" onClick={handleEditSave}>
              {t("addresses:save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { EditProviderDialog };
